import { useLazyQuery } from '@apollo/client/react';
import { Badge } from '@optools/components/ui/badge';
import { Card } from '@optools/components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@optools/components/ui/table';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import {
  MediaFeedType,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { determineGameState, GAME_STATE } from '@optools/utils/game-state';
import { Cable } from 'lucide-react';
import { useEffect } from 'react';

import { ActiveIndicator, BroadcastSourceHelp, ProgramSourceHelp } from './broadcast-source-switch';
import { RegionalBroadcastSourceControls } from './regional-broadcast-source-controls';

export interface MultiRegionBroadcastSourceControls {
  date: string;
  feedType: MediaFeedType;
  gamePk: number;
  mediaId: string;
  pipeline: BtcSchedulePipeline;
}

export function MultiRegionBroadcastSourceControls({
  date,
  feedType,
  gamePk,
  mediaId,
  pipeline,
}: MultiRegionBroadcastSourceControls) {
  const { regionalPipelines, streamState } = pipeline;

  const [refreshPipelines, { called, loading: refreshPipelinesLoading, refetch }] = useLazyQuery(
    BTC_GAME_PIPELINE,
    {
      fetchPolicy: 'network-only',
    },
  );

  const gameState = determineGameState(pipeline);

  const programSourceActive =
    gameState === GAME_STATE.STAY_TUNED || streamState === StreamState.STREAM_OFF;

  const enableProgramSource =
    gameState === GAME_STATE.STAY_TUNED || streamState === StreamState.STREAM_OFF;

  useEffect(() => {
    void (called ? refetch() : refreshPipelines({ variables: { gamePk, scheduleDate: date } }));
  }, [called, date, gamePk, gameState, refetch, refreshPipelines]);

  return (
    <Card className="flex flex-col items-start justify-between gap-6">
      <div className="flex w-full items-start justify-between gap-4 p-4">
        <div className="flex items-center gap-2">
          <div className="rounded border border-accent-foreground/70 p-1">
            <Cable className="size-4" />
          </div>
          <h2 className="font-bold">Broadcast Sources</h2>
        </div>
        <div className="flex flex-col items-start gap-2">
          <span className="text-xs font-semibold">Active Sources:</span>
          {pipeline.regionalPipelines.map((regionalPipeline) => {
            return (
              <div
                key={regionalPipeline.id}
                className="flex w-full items-center gap-4"
                data-testid={`${regionalPipeline.id}-active-source`}>
                <h3 className="w-14 text-xs font-bold">{regionalPipeline.region}:</h3>
                <Badge>{regionalPipeline.activeSource}</Badge>
              </div>
            );
          })}
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="divide-x border-t">
            <TableHead className="font-bold">Region</TableHead>
            {enableProgramSource && (
              <TableHead>
                <div className="flex items-center justify-between gap-2">
                  <ActiveIndicator
                    active={programSourceActive}
                    data-testid="program-source-active-indicator"
                  />
                  <span>Program Source</span>
                  <ProgramSourceHelp />
                </div>
              </TableHead>
            )}
            <TableHead>
              <div className="flex items-center justify-between gap-2">
                <ActiveIndicator
                  active={gameState === GAME_STATE.PRE_GAME}
                  data-testid="pre-game-source-active-indicator"
                />
                <span className="font-bold">Pre-Game</span>
                <BroadcastSourceHelp active={gameState === GAME_STATE.PRE_GAME} />
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center justify-between gap-2">
                <ActiveIndicator
                  active={gameState === GAME_STATE.GAME}
                  data-testid="game-source-active-indicator"
                />
                <span className="font-bold">Game</span>
                <BroadcastSourceHelp active={gameState === GAME_STATE.GAME} />
              </div>
            </TableHead>
            <TableHead>
              <div className="flex items-center justify-between gap-2">
                <ActiveIndicator
                  active={gameState === GAME_STATE.POST_GAME}
                  data-testid="post-game-source-active-indicator"
                />
                <span className="font-bold">Post-Game</span>
                <BroadcastSourceHelp active={gameState === GAME_STATE.POST_GAME} />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {regionalPipelines.map((regionalPipeline) => (
            <RegionalBroadcastSourceControls
              key={regionalPipeline.id}
              date={date}
              enableProgramSource={enableProgramSource}
              feedType={feedType}
              gamePk={gamePk}
              isRefreshing={refreshPipelinesLoading}
              mediaId={mediaId}
              pipeline={regionalPipeline}
              streamState={streamState}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
